import { queryClient } from '@/shared/api/queryClient';
import { type InfiniteData, type QueryKey } from '@tanstack/react-query';
import type { FileListItem } from 'quadratic-shared/typesAndSchemas';
import type { DocumentListItem } from 'quadratic-shared/typesAndSchemasDocuments';
import { useMemo } from 'react';

type FileListPage = { items: FileListItem[]; nextCursor: string | null };
type FileListInfiniteData = InfiniteData<FileListPage>;

type DocumentListPage = { items: DocumentListItem[]; nextCursor: string | null };
type DocumentListInfiniteData = InfiniteData<DocumentListPage>;

const fileListCacheFilters = (teamUuid?: string): Array<{ queryKey: QueryKey }> =>
  teamUuid
    ? [{ queryKey: ['recentFiles', teamUuid] }, { queryKey: ['folderFiles', teamUuid] }]
    : [{ queryKey: ['recentFiles'] }, { queryKey: ['folderFiles'] }];

export const getDuplicatePlaceholderUuid = (sourceUuid: string) => `${sourceUuid}--duplicate`;

/**
 * Centralized invalidation for the dashboard's file-list React Query caches
 * (`useRecentFilesQuery` and `useFolderFilesQuery`).
 *
 * Call this after any mutation that could change file list contents
 * (create, rename, delete, move, duplicate, restore, share-toggle, etc.).
 *
 * Safe to call outside React (e.g. from a route loader) — the QueryClient is
 * a module singleton shared with the `<QueryClientProvider>` in `index.tsx`.
 */
export const invalidateFileLists = (teamUuid?: string) => {
  if (teamUuid) {
    queryClient.invalidateQueries({ queryKey: ['recentFiles', teamUuid] });
    queryClient.invalidateQueries({ queryKey: ['folderFiles', teamUuid] });
    queryClient.invalidateQueries({ queryKey: ['documents', teamUuid] });
  } else {
    queryClient.invalidateQueries({ queryKey: ['recentFiles'] });
    queryClient.invalidateQueries({ queryKey: ['folderFiles'] });
    queryClient.invalidateQueries({ queryKey: ['documents'] });
  }
};

/**
 * Optimistically removes one or more files from every active file-list
 * React Query cache so rows disappear from the dashboard the moment the
 * mutation starts, rather than after the API and the subsequent refetch
 * both complete.
 *
 * Returns a revert function that restores the previous cache state. Callers
 * should invoke it from a catch block when the underlying API call fails so
 * the removed rows reappear.
 *
 * Pass `teamUuid` when known to scope the cache scan; omit to scan every team.
 */
export const removeFilesFromCacheOptimistic = (uuids: string[], teamUuid?: string): (() => void) => {
  if (uuids.length === 0) return () => {};
  const uuidSet = new Set(uuids);

  const filters = fileListCacheFilters(teamUuid);

  const snapshots: Array<[QueryKey, FileListInfiniteData | undefined]> = [];

  for (const filter of filters) {
    const matched = queryClient.getQueriesData<FileListInfiniteData>(filter);
    for (const [key, data] of matched) {
      snapshots.push([key, data]);
      if (!data) continue;
      const next: FileListInfiniteData = {
        ...data,
        pages: data.pages.map((page) => ({
          ...page,
          items: page.items.filter((item) => !uuidSet.has(item.uuid)),
        })),
      };
      queryClient.setQueryData(key, next);
    }
  }

  return () => {
    for (const [key, prevData] of snapshots) {
      queryClient.setQueryData(key, prevData);
    }
  };
};

/**
 * Prepends a file to every file-list cache that currently contains `sourceUuid`.
 */
export const addFileToCacheOptimistic = (sourceUuid: string, file: FileListItem, teamUuid?: string): void => {
  for (const filter of fileListCacheFilters(teamUuid)) {
    for (const [key, data] of queryClient.getQueriesData<FileListInfiniteData>(filter)) {
      if (!data) continue;
      const items = data.pages.flatMap((page) => page.items);
      if (items.some((item) => item.uuid === file.uuid)) continue;
      if (!items.some((item) => item.uuid === sourceUuid)) continue;
      queryClient.setQueryData<FileListInfiniteData>(key, {
        ...data,
        pages: data.pages.map((page, i) => (i === 0 ? { ...page, items: [file, ...page.items] } : page)),
      });
    }
  }
};

/**
 * Documents-list counterpart to `removeFilesFromCacheOptimistic`, for the
 * `useDocumentsQuery` caches behind the Drive page, the in-sheet documents
 * panel, and the drive pickers.
 */
export const removeDocumentsFromCacheOptimistic = (uuids: string[], teamUuid?: string): (() => void) => {
  if (uuids.length === 0) return () => {};
  const uuidSet = new Set(uuids);

  const filter: { queryKey: QueryKey } = teamUuid ? { queryKey: ['documents', teamUuid] } : { queryKey: ['documents'] };

  const snapshots: Array<[QueryKey, DocumentListInfiniteData | undefined]> = [];

  const matched = queryClient.getQueriesData<DocumentListInfiniteData>(filter);
  for (const [key, data] of matched) {
    snapshots.push([key, data]);
    if (!data) continue;
    const next: DocumentListInfiniteData = {
      ...data,
      pages: data.pages.map((page) => ({
        ...page,
        items: page.items.filter((item) => !uuidSet.has(item.uuid)),
      })),
    };
    queryClient.setQueryData(key, next);
  }

  return () => {
    for (const [key, prevData] of snapshots) {
      queryClient.setQueryData(key, prevData);
    }
  };
};

/**
 * React-hook wrapper around `invalidateFileLists`,
 * `removeFilesFromCacheOptimistic`, `addFileToCacheOptimistic`, and
 * `removeDocumentsFromCacheOptimistic`. Returned values are stable across
 * renders so they're safe to drop into `useEffect`/`useCallback` deps.
 *
 * Components that aren't inside React can call the standalone functions
 * directly; both share the same singleton QueryClient.
 */
export const useFileMutationInvalidation = () => {
  return useMemo(
    () => ({
      invalidateFileLists,
      removeFilesFromCacheOptimistic,
      addFileToCacheOptimistic,
      removeDocumentsFromCacheOptimistic,
    }),
    []
  );
};
